// "Open in <broker>": the member's broker (Settings) opens the same stock in its app or site, and
// the menu offers the others. MoneyHQ only opens the page; it never places orders.
import { ChevronDownIcon, ExternalLinkIcon, SettingsIcon } from 'lucide-react';
import { BROKERS, brokerLink } from '@planb/core';
import { Link } from '@/lib/nav';
import { useMe } from '@/lib/client/use-me';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

/** compact (phones): a smaller, icon-only button (the app is named in its label), so the stock's name keeps the room. */
export function OpenInBroker({ symbol, query, compact = false }: { symbol: string; query: string; compact?: boolean }) {
  const h = compact ? 'h-8' : 'h-10';
  const pref = useMe()?.prefs.broker;
  const mine = BROKERS.find((b) => b.id === pref);
  const myLink = mine ? brokerLink(mine.id, symbol) : null;
  const others = BROKERS.map((b) => ({ ...b, href: brokerLink(b.id, symbol) })).filter((b) => b.href && b.id !== (myLink ? mine?.id : ''));
  const menu = (
    <DropdownMenuContent align="end" className="min-w-56">
      <DropdownMenuLabel className="font-normal text-muted-foreground">Open {symbol} in</DropdownMenuLabel>
      {others.map((b) => (
        <DropdownMenuItem key={b.id} asChild>
          <a href={b.href!} target="_blank" rel="noopener noreferrer"><ExternalLinkIcon />{b.app}{b.app !== b.name ? ` (${b.name})` : ''}</a>
        </DropdownMenuItem>
      ))}
      <DropdownMenuSeparator />
      <DropdownMenuItem asChild><Link href={`/settings${query}`}><SettingsIcon />{mine ? 'Change your broker' : 'Set your broker'}</Link></DropdownMenuItem>
      <p className="px-2 pt-1 pb-1.5 text-[11px] text-muted-foreground">Opens the stock’s page there. MoneyHQ isn’t affiliated with any broker.</p>
    </DropdownMenuContent>
  );
  if (mine && myLink) {
    return (
      <div className="inline-flex">
        <Button asChild variant="outline" className={`${h} rounded-l-full rounded-r-none border-r-0 ${compact ? 'px-2.5' : 'px-4'}`}>
          <a href={myLink} target="_blank" rel="noopener noreferrer" title={`Open ${symbol} in ${mine.app}`} aria-label={compact ? `Open in ${mine.app}` : undefined}><ExternalLinkIcon />{!compact && `Open in ${mine.app}`}</a>
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className={`${h} rounded-l-none rounded-r-full ${compact ? 'px-2' : 'px-2.5'}`} aria-label="Other brokers"><ChevronDownIcon /></Button>
          </DropdownMenuTrigger>
          {menu}
        </DropdownMenu>
      </div>
    );
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={`${h} rounded-full ${compact ? 'px-2.5' : 'px-4'}`} aria-label={compact ? 'Open in a broker' : undefined}><ExternalLinkIcon />{!compact && 'Open in…'}<ChevronDownIcon /></Button>
      </DropdownMenuTrigger>
      {menu}
    </DropdownMenu>
  );
}
